import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../app/store";
import {
  setProposedValue,
  setProposedType,
  setMarkupValue,
  setManualVol,
  setMixBasis,
  setSelMonth,
  selectActiveVolShares,
  selectTxnShares,
  selectDefs,
  selectPrices,
  selectOrder,
  selectGroupBuckets,
  selectMarkups,
  selectKlearlyTake,
  selectPriceFloors,
  selectTPV,
  selectTxns,
  selectMixWeights,
  selectMixWeightTotal,
} from "../../pricingToolSlice";
import type { MixBasis } from "../../pricingToolSlice";
import Seg from "../shared/Seg";
import { monthShort, monthLong, shortDate } from "../shared/formatters";
import PriceRow from "./PriceRow";
import styles from "../../PricingTool.module.css";

export default function PricingPerGroup() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    source,
    channel,
    selMonth,
    manualVol,
    mixBasis,
    apiMonthly,
    apiRange,
  } = useSelector((s: RootState) => s.pricingTool);

  const order = useSelector(selectOrder);
  const defs = useSelector(selectDefs);
  const prices = useSelector(selectPrices);
  const volShares = useSelector(selectActiveVolShares);
  const txnShares = useSelector(selectTxnShares);
  const groupBuckets = useSelector(selectGroupBuckets);
  const markups = useSelector(selectMarkups);
  const takes = useSelector(selectKlearlyTake);
  const floors = useSelector(selectPriceFloors);
  const tpv = useSelector(selectTPV);
  const txns = useSelector(selectTxns);
  const mixWeights = useSelector(selectMixWeights);
  const byCount = mixBasis === "count";
  const weightTotal = useSelector(selectMixWeightTotal);

  // Which rows have their components open. Kept here rather than in the store:
  // it is a view of the row, not part of the proposal, and the edits made
  // through it live in the store on their own.
  const [openParts, setOpenParts] = useState<Record<string, boolean>>({});
  // An open markup panel belongs to the source it was opened on. Left open
  // across a switch, Manual's empty panel would read as a contract.
  useEffect(() => {
    setOpenParts({});
  }, [source]);

  // Only show the seasonality picker when the server returned real per-month data.
  const months = source !== "manual" && apiMonthly ? apiMonthly : [];
  const hasMonthly = months.length > 0;
  const activeMonth = months.some((m) => m.month === selMonth)
    ? selMonth
    : null;
  const activeEntry = months.find((m) => m.month === activeMonth) ?? null;
  const hasProjected = months.some((m) => m.projected);
  const maxTpv = Math.max(1, ...months.map((m) => m.avgTpv));
  const rangeLabel = apiRange
    ? `${shortDate(apiRange.start)} – ${shortDate(apiRange.end)}`
    : "Whole period";

  // Only the edited group is written. The others keep exactly the number they
  // were given — the old handler rescaled every one of them on each keystroke
  // to force a total of 100, which is what made the boxes move by themselves.
  // Weights that do not total 100 are fine: the shares are normalised on read.
  const setManualShare = (g: string, raw: number) => {
    const next = { ...(manualVol || mixWeights) };
    next[g] = Math.max(0, Math.min(100, raw));
    dispatch(setManualVol(next));
  };

  return (
    <>
      {/* Header */}
      <div className={styles.groups_header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: "#f0f0ff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6B4EFF"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </span>
          <span
            style={{
              fontSize: 11,
              color: "rgba(0,0,0,0.45)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            Pricing per group
          </span>
          <span style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }}>
            · the merchant's customers
          </span>
        </div>
        {channel === "online" && (
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#6B4EFF",
              background: "#f0f0ff",
              border: "1px solid #e6e6ff",
              borderRadius: 8,
              padding: "7px 14px",
            }}
          >
            Adyen Online · 3 buckets
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          margin: "0 0 8px",
        }}
      >
        <span style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
          Mix typed as
        </span>
        <Seg
          sm
          opts={[
            { key: "volume", label: "% of volume" },
            { key: "count", label: "% of transactions" },
          ]}
          active={mixBasis}
          onSel={(k) => dispatch(setMixBasis(k as MixBasis))}
        />
        {manualVol && (
          <button
            type="button"
            onClick={() => dispatch(setManualVol(null))}
            style={{
              fontSize: 11,
              color: "#6B4EFF",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {source === "manual" ? "Reset mix" : "Back to measured mix"}
          </button>
        )}
      </div>

      <p style={{ margin: "0 0 8px", fontSize: 11, color: "#6B4EFF" }}>
        {mixBasis === "count"
          ? "Volume shares are derived from the transaction mix using each group's average ticket size."
          : "Fixed-fee groups are distributed by transaction count; percentage groups by volume."}
        {Math.abs(weightTotal - 100) > 0.5 &&
          ` Typed weights total ${weightTotal.toFixed(0)}% — shares below are scaled to 100%.`}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(104px,0.9fr) 72px 92px minmax(176px,1.4fr)",
          gap: 16,
          alignItems: "end",
        }}
      >
        <span className={styles.groups_col_header}>Pricing group</span>
        <span className={styles.groups_col_header}>
          {byCount ? "% of txns" : "% of volume"}
        </span>
        <span className={styles.groups_col_header}>
          {byCount ? "Transactions" : "Volume"}
        </span>
        <span className={styles.groups_col_header}>Proposed price</span>
      </div>

      {order.map((g) => (
        <PriceRow
          key={g}
          def={defs[g]}
          buckets={groupBuckets[g] || []}
          price={prices[g]}
          volShare={volShares[g] || 0}
          txnShare={txnShares[g] || 0}
          groupVolume={(tpv * (volShares[g] || 0)) / 100}
          groupTxns={(txns * (txnShares[g] || 0)) / 100}
          byCount={byCount}
          editable
          manualPct={mixWeights[g]}
          manualBasis={byCount ? "of txns" : "of volume"}
          onManualPct={(v) => setManualShare(g, v)}
          onSetVal={(v) =>
            dispatch(
              setProposedValue({
                key: g,
                value: v,
                valueType: prices[g].valueType,
              })
            )
          }
          onSetType={(t) =>
            dispatch(
              setProposedType({ key: g, value: prices[g].value, valueType: t })
            )
          }
          markup={markups[g]}
          take={takes[g]}
          floor={floors[g]}
          open={!!openParts[g]}
          manualEntry={source === "manual"}
          onToggle={() => setOpenParts((prev) => ({ ...prev, [g]: !prev[g] }))}
          onSetMarkup={(part, v) =>
            dispatch(
              setMarkupValue({
                key: g,
                part,
                value: v,
                valueType: prices[g].valueType,
              })
            )
          }
        />
      ))}

      {/* Seasonality */}
      {hasMonthly && (
        <>
          <div className={styles.seasonality_row}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "rgba(0,0,0,0.88)",
                }}
              >
                Seasonality
              </span>
              <span style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                {activeMonth
                  ? activeEntry?.projected
                    ? "Single month, still in progress"
                    : "Single month from connected data"
                  : `Monthly average · ${rangeLabel}${hasProjected ? " · current month projected" : ""}`}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: "#6B4EFF", fontWeight: 500 }}>
                {activeMonth
                  ? `Modelling ${monthLong(activeMonth)}${activeEntry?.projected ? " (projected)" : ""}`
                  : "Modelling all months"}
              </span>
              {activeMonth && (
                <button
                  type="button"
                  className={styles.month_clear_btn}
                  onClick={() => dispatch(setSelMonth(null))}
                >
                  Clear ✕
                </button>
              )}
            </div>
          </div>
          <div className={styles.month_bars}>
            {months.map((m) => {
              const selected = m.month === activeMonth;
              return (
                <button
                  key={m.month}
                  className={styles.month_bar_btn}
                  title={
                    m.projected
                      ? `${monthLong(m.month)} · projected from the first ${m.projected.daysCounted} of ${m.projected.daysInMonth} days`
                      : monthLong(m.month)
                  }
                  onClick={() =>
                    dispatch(setSelMonth(selected ? null : m.month))
                  }
                >
                  <div
                    className={styles.month_bar}
                    style={{
                      height: Math.max(4, Math.round((m.avgTpv / maxTpv) * 36)),
                      // Hatched rather than solid: this month is a projection
                      // from part of a month, not a settled figure.
                      background: selected
                        ? "linear-gradient(180deg,#3F59FB,#9F20FF)"
                        : m.projected
                          ? "repeating-linear-gradient(135deg,#e6e6ff 0 3px,#c3c3f5 3px 6px)"
                          : "#e6e6ff",
                    }}
                  />
                  <span
                    className={styles.month_label}
                    style={{
                      color: selected ? "#6B4EFF" : "rgba(0,0,0,0.4)",
                      fontWeight: selected ? 600 : 400,
                    }}
                  >
                    {monthShort(m.month)}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
